import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import { capitalize } from '@brightlocal/shared';

import { routes } from '../routes';

type RouteBuilder = typeof routes;
type RouteName = keyof RouteBuilder;

/** Optional flags passed as the last argument to a `to*` navigation helper. */
export interface AppNavigateOptions {
  /** When true, replaces the current history entry instead of pushing a new one. */
  replace?: boolean;
}

/** Route-builder args plus an optional trailing `{ replace }` flag. */
type ArgsWithNavigateOptions<P extends unknown[]> = P extends []
  ? [options?: AppNavigateOptions]
  : [...P, options?: AppNavigateOptions];

/** Navigation helper types derived from the route config (e.g. toHome, toSignUp, toSignUpWithId). */
type NavigationHelpers = {
  [K in RouteName as `to${Capitalize<K>}`]: (...args: ArgsWithNavigateOptions<Parameters<RouteBuilder[K]>>) => void;
};

const isAppNavigateOptions = (value: unknown): value is AppNavigateOptions =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.keys(value).every((key) => key === 'replace');

/**
 * Type-safe navigation helpers for all application routes.
 * Helpers are generated from the route config in `shared/routes.ts`.
 * To add a new route, add it to the `routes` object there.
 *
 * Pass `{ replace: true }` as the last argument to replace the current history
 * entry (e.g. post-login or wizard step transitions that should not stack).
 *
 * @returns An object of navigation functions (toHome, toSignUp, toSignUpWithId, etc.)
 *
 * @example
 * ```tsx
 * const { toHome, toSignUp, toSignUpWithId, toLocations } = useAppNavigation();
 *
 * toHome();
 * toSignUp();
 * toSignUpWithId(123);
 * toLocations({ replace: true });
 * ```
 */
export const useAppNavigation = (): NavigationHelpers => {
  const navigate = useNavigate();

  return useMemo(() => {
    const navigationHelpers = {} as Record<string, (...args: unknown[]) => void>;

    // Generate navigation functions from the route config
    (Object.keys(routes) as RouteName[]).forEach((routeName) => {
      const routeBuilder = routes[routeName];
      const helperName = `to${capitalize(routeName)}`;

      navigationHelpers[helperName] = (...args: unknown[]) => {
        const maybeOptions = args.at(-1);
        const hasOptions = isAppNavigateOptions(maybeOptions);
        const routeArgs = hasOptions ? args.slice(0, -1) : args;
        const { replace } = hasOptions ? maybeOptions : {};

        // TypeScript can't infer the exact signature dynamically, so we use unknown
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const routeConfig = (routeBuilder as any)(...routeArgs);

        // Handle both simple string paths and objects with to/params
        if (typeof routeConfig === 'string') {
          void navigate(replace ? { to: routeConfig, replace } : { to: routeConfig });
        } else {
          void navigate(replace ? { ...routeConfig, replace } : routeConfig);
        }
      };
    });

    return navigationHelpers as NavigationHelpers;
  }, [navigate]);
};
